import { BreakdownAction } from '@dealstack/frontend/app/pages/Valuations/components/BreakdownPopover/BreakdownAction';
import { BreakdownPopover } from '@dealstack/frontend/app/pages/Valuations/components/BreakdownPopover/BreakdownPopover';
import { CopyAction } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/CopyAction';
import { DefaultHeaderCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/DefaultHeaderCell';
import { NewDefaultCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/NewDefaultCell';
import { useMemo } from 'react';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { NewGlobalValuationRow } from './toGlobalTableData';
import { CurrencyType, SecurityBreakdown } from '@dealstack/shared/lib/generated';
import {
	formatSummaryDecimal,
	formatSummaryNumber,
} from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import { ColumnDef } from '@tanstack/react-table';
import type { CreateGlobalTableColumnsParams } from './GlobalValuationTable.utils.types';
import { NewEntityCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/NewEntityCell';
import { TableColumnBuilder } from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder';
import { ColumnBuilderConfig } from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder.types';
import { isDisplayValueValid } from '@dealstack/frontend/app/pages/Valuations/components/table/utils/displayValue';

const PERSON_WITHOUT_GROUPS_SUFFIX = 'person_without_group_identifier';

export const subColumnsId = (params: { mipId: string; personId: string }): string => {
	return `${params.mipId}.${params.personId}`;
};

function buildSecuritiesBreakdownAction(
	matched: SecurityBreakdown[],
	isPerson: boolean,
	currency: CurrencyType | undefined,
	isHovered: boolean
) {
	if (matched.length === 0) {
		return null;
	}
	return (
		<BreakdownAction
			key="breakdown"
			isVisible={isHovered}
			renderContent={(close) => (
				<BreakdownPopover
					items={matched.map((s) => ({
						name: s.name,
						units: new HighPrecisionDecimal(s.units),
						amount: new HighPrecisionDecimal(s.amount),
						issuerId: s.issuerId,
					}))}
					showSecurityName={isPerson}
					showTotal={isPerson}
					currency={currency}
					onClose={close}
				/>
			)}
		/>
	);
}

const formatValuationValue = (value: unknown) => {
	if (value === 0 || value === '') {
		return '';
	}
	if (typeof value === 'number') {
		return formatSummaryNumber(value);
	}

	if (value instanceof HighPrecisionDecimal) {
		return formatSummaryDecimal(value);
	}
	return '';
};

export function createGlobalTableColumns(
	params: CreateGlobalTableColumnsParams,
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>
): ColumnDef<NewGlobalValuationRow>[] {
	const config: ColumnBuilderConfig<NewGlobalValuationRow> = {
		showFilterInputs: params.showFilterInputs,
		columnFilters: params.columnFilters,
		updateColumnFilter: params.updateColumnFilter,
		toggleSubColumns: params.toggleSubColumns,
		hasVisibleSubColumns: params.hasVisibleSubColumns,
		isLastSubColumn: params.isLastSubColumn,
		components: {
			HeaderCell: DefaultHeaderCell,
			DefaultCell: NewDefaultCell,
		},
		defaultRenderActions: (_row, value, isHovered, formatter) => {
			const data = formatter ? formatter(value) : value;
			if (!isDisplayValueValid(data)) {
				return null;
			}
			const text = typeof data === 'string' ? data : String(data);
			return <CopyAction text={text} isHovered={isHovered} />;
		},
	};

	const builder = new TableColumnBuilder<NewGlobalValuationRow>(config);

	// Add base columns
	builder
		.addColumnWithCustomCell({
			id: 'entity',
			header: tValuations('summary.global.person_name_header'),
			accessor: (row) => row.entity,
			cellComponent: NewEntityCell,
			cellPropsMapper: ({ value, tanstackRow, isLastRow, isLastColumn, columnIndex }) => ({
				value: value as string | number | null,
				row: tanstackRow,
				isLastRow,
				isLastColumn,
				isExpanded: tanstackRow.getIsExpanded(),
				columnIndex,
			}),
		})
		.addDefaultColumn({
			id: 'total',
			header: tValuations('summary.global.distributed_header', {
				currency: params.summaryData?.currency ?? '',
			}),
			accessor: (row) => row.total,
			alignment: 'end',
			formatter: formatValuationValue,
			renderActions: (row: NewGlobalValuationRow, _value, isHovered) => {
				const breakdown = row.metadata?.securitiesBreakdown ?? [];
				return buildSecuritiesBreakdownAction(
					breakdown,
					row.metadata?.type === 'person',
					params.summaryData?.currency,
					isHovered
				);
			},
		});

	// Add dynamic person/group columns
	if (params.summaryData?.groups) {
		const filterGroups = params.summaryData.groups.filter((group) => group.generation > 0);
		// Filter out the top level group
		filterGroups.forEach((group, index) => {
			const isLastColumn = index === filterGroups.length - 1;
			const mipId = group.id;
			if (!mipId) {
				// Individual persons without groups
				group.persons.forEach((person) => {
					const personId = person.id;
					builder.addDefaultColumn({
						id: personId + PERSON_WITHOUT_GROUPS_SUFFIX,
						header: `${person.displayName} (${params.summaryData?.currency})`,
						accessor: (row) => row[personId] ?? 0,
						alignment: 'end',
						formatter: formatValuationValue,
						isLastColumn,
						renderActions: (row: NewGlobalValuationRow, _value, isHovered) => {
							const breakdown = row.metadata?.securitiesBreakdown ?? [];
							const matched = breakdown.filter(
								(s) => s.holderId === personId && !s.holderGroupId
							);
							return buildSecuritiesBreakdownAction(
								matched,
								row.metadata?.type === 'person',
								params.summaryData?.currency,
								isHovered
							);
						},
					});
				});
			} else if (mipId) {
				// Groups with sub-columns
				builder.addColumnWithSubColumns({
					id: mipId,
					header: `${group.name} (${params.summaryData?.currency})`,
					accessor: (row) => {
						const hasVisibleSubColumns = group.persons.some((person) => {
							const subColId = subColumnsId({ mipId, personId: person.id });
							return !!params.columnVisibility?.[subColId];
						});

						return hasVisibleSubColumns ? '' : row[mipId] ?? 0;
					},
					alignment: 'end',
					enableExpanding: true,
					formatter: formatValuationValue,
					renderActions: (row: NewGlobalValuationRow, _value, isHovered) => {
						const breakdown = row.metadata?.securitiesBreakdown ?? [];
						const matched = breakdown.filter((s) => s.holderGroupId === mipId);
						return buildSecuritiesBreakdownAction(
							matched,
							row.metadata?.type === 'person',
							params.summaryData?.currency,
							isHovered
						);
					},
					subColumns: group.persons.map((person) => {
						const personId = person.id;
						return {
							id: subColumnsId({ mipId, personId }),
							header: person.displayName,
							accessor: (row) => row[`${mipId}.${personId}`] ?? 0,
							alignment: 'end',
							formatter: formatValuationValue,
							renderActions: (row: NewGlobalValuationRow, _value, isHovered) => {
								const breakdown = row.metadata?.securitiesBreakdown ?? [];
								const matched = breakdown.filter(
									(s) => s.holderGroupId === mipId && s.holderId === personId
								);
								return buildSecuritiesBreakdownAction(
									matched,
									row.metadata?.type === 'person',
									params.summaryData?.currency,
									isHovered
								);
							},
						};
					}),
					isLastColumn,
				});
			}
		});
	}

	return builder.build();
}

export function useGlobalValuationTableColumns({
	showFilterInputs,
	columnFilters,
	updateColumnFilter,
	toggleSubColumns,
	hasVisibleSubColumns,
	isLastSubColumn,
	summaryData,
	expandedState,
	columnVisibility,
}: CreateGlobalTableColumnsParams): ColumnDef<NewGlobalValuationRow>[] {
	const tValuations = useTranslationValuations();

	const columns = useMemo(() => {
		return createGlobalTableColumns(
			{
				showFilterInputs,
				columnFilters,
				updateColumnFilter,
				toggleSubColumns,
				hasVisibleSubColumns,
				isLastSubColumn,
				summaryData,
				expandedState,
				columnVisibility,
			},
			tValuations
		);
	}, [
		showFilterInputs,
		columnFilters,
		updateColumnFilter,
		toggleSubColumns,
		hasVisibleSubColumns,
		isLastSubColumn,
		summaryData,
		expandedState,
		columnVisibility,
		tValuations,
	]);

	return columns;
}
